import React from "react";
import { Link } from "react-router-dom";
import HeroSlider from "../components/HeroSlider";
import SectionHeading from "../components/SectionHeading";
import CTABanner from "../components/CTABanner";
import {
    Plant, Wall, Snowflake, Drop, HardHat, Leaf, Sparkle, Flower,
    Buildings, ArrowRight, Quotes, Star, Phone
} from "@phosphor-icons/react";
import {
    SERVICES_RESIDENTIAL, SERVICES_COMMERCIAL, PROCESS_STEPS,
    PORTFOLIO_ITEMS, TESTIMONIALS, STATS, BRAND, PHOTOS
} from "../lib/data";

const ICONS = { Plant, Wall, Snowflake, Drop, HardHat, Leaf, Sparkle, Flower, Buildings };

function ServiceCard({ s }) {
    const Icon = ICONS[s.icon] || Plant;
    return (
        <div data-testid={`home-service-${s.title}`} className="group relative overflow-hidden rounded-2xl bg-brand-surface border border-brand-border hover:-translate-y-1 hover:shadow-xl transition-all duration-500">
            <div className="aspect-[4/3] overflow-hidden">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-brand-surfaceAlt text-brand-primary">
                        <Icon size={22} weight="duotone" />
                    </div>
                    <h3 className="font-serif text-xl text-brand-primary font-semibold">{s.title}</h3>
                </div>
                <p className="text-sm text-brand-textMuted leading-relaxed">{s.desc}</p>
            </div>
        </div>
    );
}

export default function HomePage() {
    const featuredServices = SERVICES_RESIDENTIAL.slice(0, 4);
    const featuredPortfolio = PORTFOLIO_ITEMS.slice(0, 6);

    return (
        <div data-testid="home-page">
            <HeroSlider />

            {/* Stats strip */}
            <section className="border-b border-brand-border bg-brand-surfaceAlt">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {STATS.map((s) => (
                        <div key={s.label} className="text-center sm:text-left">
                            <div className="font-serif text-3xl sm:text-4xl text-brand-primary font-semibold">{s.value}</div>
                            <div className="text-xs uppercase tracking-[0.2em] text-brand-textMuted mt-1">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Intro */}
            <section className="px-4 sm:px-8 py-24">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <p className="text-xs uppercase tracking-[0.3em] text-brand-secondary font-bold mb-4">Who we are</p>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-primary leading-[1.05] font-medium tracking-tight">
                            A quietly obsessive landscape & hardscape studio,
                            serving the DMV since 1998.
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:pl-8">
                        <p className="text-base sm:text-lg text-brand-textMuted leading-relaxed">
                            We design, build, and maintain outdoor spaces that age beautifully — for homeowners, HOAs, and commercial properties. The same family-run team you start with is the one that finishes the job, and the one you call ten years later.
                        </p>
                        <Link to="/about" data-testid="home-about-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-primary hover:gap-3 transition-all">
                            Read our story <ArrowRight size={16} weight="bold" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Services */}
            <section id="services" className="px-4 sm:px-8 py-24 bg-brand-surfaceAlt">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
                        <SectionHeading
                            eyebrow="What we do"
                            title="Full-service from sketch to last leaf of fall."
                            sub="Five generations of craft, distilled into one trusted partner for design, build, and maintenance."
                        />
                        <Link to="/services" data-testid="home-services-link" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-primary hover:gap-3 transition-all">
                            Explore all services <ArrowRight size={16} weight="bold" />
                        </Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-6 lg:gap-8">
                        {featuredServices.map((s) => <ServiceCard key={s.title} s={s} />)}
                    </div>
                </div>
            </section>

            {/* Featured Portfolio */}
            <section className="px-4 sm:px-8 py-24">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
                        <SectionHeading
                            eyebrow="Selected projects"
                            title="Spaces our neighbours actually use."
                            sub="A small selection of recent residential and commercial work across Northern Virginia."
                        />
                        <Link to="/gallery" data-testid="home-gallery-link" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-primary hover:gap-3 transition-all">
                            View full gallery <ArrowRight size={16} weight="bold" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {featuredPortfolio.map((p, i) => (
                            <div
                                key={p.id}
                                data-testid={`home-portfolio-${p.id}`}
                                className={`relative overflow-hidden rounded-xl group ${i === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-square"}`}
                            >
                                <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[900ms]" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="text-xs uppercase tracking-[0.2em] text-[#D6E0CF]">{p.category}</div>
                                    <div className="font-serif text-lg">{p.title}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process timeline */}
            <section className="px-4 sm:px-8 py-24 bg-brand-primary text-white">
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-3xl mb-14">
                        <p className="text-xs uppercase tracking-[0.3em] text-brand-secondary font-bold mb-4">Start to finish</p>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-medium tracking-tight">
                            From concept to completion.
                        </h2>
                        <p className="mt-5 text-base sm:text-lg text-[#D6D3C2] max-w-xl">
                            Five clear stages. One dedicated crew lead. Every detail signed off before we move on.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 rounded-2xl overflow-hidden">
                        {PROCESS_STEPS.map((step) => (
                            <div key={step.n} data-testid={`home-step-${step.n}`} className="bg-brand-primary p-7 hover:bg-brand-primaryHover transition-colors">
                                <div className="text-xs uppercase tracking-[0.3em] text-brand-secondary font-bold">Step {step.n}</div>
                                <h3 className="font-serif text-2xl mt-3 mb-3">{step.title}</h3>
                                <p className="text-sm text-[#D6D3C2] leading-relaxed">{step.desc}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-12">
                        <Link to="/process" data-testid="home-process-link" className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:gap-3 transition-all">
                            See the full process <ArrowRight size={16} weight="bold" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Why choose us */}
            <section className="px-4 sm:px-8 py-24">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
                    <div className="relative aspect-[4/5] rounded-2xl overflow-hidden">
                        <img src={PHOTOS.lushFloweringGarden} alt="Crafted landscape" className="w-full h-full object-cover" />
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-brand-secondary font-bold mb-4">Why Great Outdoors</p>
                        <h2 className="font-serif text-4xl sm:text-5xl text-brand-primary leading-[1.05] font-medium tracking-tight">
                            The kind of company you only need to hire once.
                        </h2>
                        <ul className="mt-8 space-y-5">
                            {[
                                ["27+ years in the DMV", "Established 1998. Generations of repeat clients."],
                                ["Our crews — never strangers", "We don't sub out the work. The team you meet is the team that builds it."],
                                ["Design-first thinking", "Every project starts with a plan, a budget, and a finish-line date."],
                                ["Year-round partnership", "From spring cleanups to snow response, one number to call."],
                            ].map(([t, d]) => (
                                <li key={t} className="flex gap-4">
                                    <div className="mt-1 h-2 w-2 rounded-full bg-brand-accent shrink-0" />
                                    <div>
                                        <div className="font-semibold text-brand-primary">{t}</div>
                                        <div className="text-sm text-brand-textMuted">{d}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <a href={`tel:${BRAND.officePhone.replace(/\D/g, "")}`} data-testid="home-call-cta" className="mt-9 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-primary text-white text-sm font-semibold hover:bg-brand-primaryHover transition-all hover:-translate-y-0.5">
                            <Phone size={18} weight="duotone" /> Call {BRAND.officePhone}
                        </a>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="px-4 sm:px-8 py-24 bg-brand-surfaceAlt">
                <div className="max-w-7xl mx-auto">
                    <SectionHeading
                        eyebrow="Trusted by neighbours"
                        title="The properties speak. So do the people on them."
                    />
                    <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
                        {TESTIMONIALS.map((t, i) => (
                            <figure key={i} data-testid={`home-testimonial-${i}`} className="bg-brand-surface border border-brand-border rounded-2xl p-8 hover:-translate-y-1 transition-transform duration-500">
                                <Quotes size={28} weight="fill" className="text-brand-secondary mb-5" />
                                <blockquote className="font-serif text-xl sm:text-2xl text-brand-primary leading-[1.35]">
                                    "{t.quote}"
                                </blockquote>
                                <figcaption className="mt-6 flex items-center gap-3">
                                    <div className="flex">{[...Array(5)].map((_, j) => <Star key={j} size={14} weight="fill" className="text-brand-accent" />)}</div>
                                    <div className="text-sm">
                                        <div className="font-semibold text-brand-primary">{t.name}</div>
                                        <div className="text-brand-textMuted">{t.role}</div>
                                    </div>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            <CTABanner />
        </div>
    );
}
